import { Component } from "react";
import withRouterParams from "../../../withRouterParams";
import request from "../../../request";
import swal from "sweetalert";
import { Button, capitalize, CircularProgress } from "@mui/material";
import PillLabel from "../../../components/PillLabel";
import RoundedIconButton from "../../../components/RoundedIconButton";
import Refresh from "@mui/icons-material/Refresh";
import CenteredMessage from "../../../components/CenteredMessage";
import LabeledContainer from "../../../components/LabeledContainer";
import LabelValue from "../../../components/LabelValue";
import { formatDate, formatMoney, hasIntersection } from "../../../../../shared/utils";
import { CASH_REQUISITION_STATUS, DOCUMENT_NAME, USER_ROLE } from "../../../../../shared/constants";
import Attachment from "../../../components/Attachment";
import AttachmentList from "../../../components/AttachmentList";
import { connect } from "react-redux";
import { requestConfirmation } from "../../../utils";
import Approve from "./Approve";


class _CashRequisition extends Component {

   state = {
      cashRequisition: null,
      loading: false,
      approveModalOpen: false,
   }

   openApproveModal = () => {
      this.setState({ approveModalOpen: true });
   }

   closeApproveModal = cashRequisition => {
      this.setState({ approveModalOpen: false });
      if (cashRequisition) {
         this.setState({ cashRequisition });
      }
   }

   reject = async () => {

      // confirm
      const question = `Are you sure you want to reject this cash requisition?`;
      const confirmed = await requestConfirmation(question);

      if (!confirmed)
         return;

      try {

         this.setState({ loading: true });

         const payload = {
            status: CASH_REQUISITION_STATUS.REJECTED
         }

         const res = await request.patch(`/api/finance/cash-requisitions/${this.getId()}`, payload);
         const cashRequisition = res.data;

         this.setState({ cashRequisition });

         swal("Success", "Cash requisition rejected successfully", "success");

      } catch (err) {
         swal("Error", err.message, "error");
      } finally {
         this.setState({ loading: false });
      }

   }

   getId() {
      const { id } = this.props.params;
      return id;
   }

   fetchData = async () => {

      const id = this.getId();

      try {

         this.setState({ loading: true });

         const res = await request.get(`/api/general/cash-requisitions/${id}`);
         const cashRequisition = res.data;
         this.setState({ cashRequisition });

      } catch (err) {
         swal("Error", String(err), "error");
      } finally {
         this.setState({ loading: false });
      }
   }

   componentDidMount() {
      this.fetchData();
   }

   render() {

      if (this.state.loading) {
         return <div className="flex items-center justify-center h-full">
            <CircularProgress />
         </div>
      }

      if (!this.state.cashRequisition) {
         return <div className="flex items-center justify-center h-full">
            <CenteredMessage
               message="Faield to load page"
               onRefresh={this.fetchData}
            />
         </div>
      }

      const { status, amount, created_at, purpose, attachments, payment_type, requested_by, department, approved_by } = this.state.cashRequisition;

      const receiptAttachment = attachments.find(a => a.name === DOCUMENT_NAME.CASH_REQUISITION.RECEIPT); 
      const otherAttachments = attachments.filter(a => a.name !== DOCUMENT_NAME.CASH_REQUISITION.RECEIPT);

      const isOwner = this.state.cashRequisition.requested_by._id === this.props.user._id;
      const canApproveOrReject = hasIntersection(this.props.user.roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.FINANCE_MANAGER ]) && status === CASH_REQUISITION_STATUS.PENDING;
      
      return <div className="h-full grid grid-rows-[1fr,auto] gap-4 text-gray-600">

         <div className="p-4">
            <div className="flex items-center text-xl md:text-3xl gap-2">
               Requisition <b>#{this.getId().slice(-4)}</b>
               <PillLabel color="secondary">
                  {status.toUpperCase()}
               </PillLabel>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
               <LabeledContainer label="Details" className="p-4 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <LabelValue label="Status" value={capitalize(status)} />
                  <LabelValue label="Amount" value={formatMoney(amount)} />
                  <LabelValue label="Requested By" value={`${requested_by.name} ${requested_by.surname}`} />
                  <LabelValue label="Department" value={department} />
                  <LabelValue label="Approved By" value={approved_by ? `${approved_by.name} ${approved_by.surname}` : "–"} />
                  <LabelValue label="Disbursement Method" value={payment_type || '–'} />
                  <LabelValue label="Requested" value={formatDate(created_at)} />
               </LabeledContainer>
               <LabeledContainer label="Purpose" className="p-4 rounded-lg">
                  {purpose}
               </LabeledContainer>
               <LabeledContainer label="Attachments" className="p-4 rounded-lg">
                  {
                     <Attachment
                        name={DOCUMENT_NAME.CASH_REQUISITION.RECEIPT}
                        url={receiptAttachment?.url}
                        onSave={
                           async receipt => {
                              const res = await request.put(`/api/general/cash-requisitions/${this.getId()}/receipt`, { receipt });
                              const cashRequisition = res.data;
                              this.setState({ cashRequisition });
                              swal("Success", "Receipt uploaded successfully", "success");
                           }
                        }
                        canEdit={status === CASH_REQUISITION_STATUS.APPROVED && isOwner && !receiptAttachment}
                     />
                  }
                  <AttachmentList
                     attachments={otherAttachments}
                     variant="grid"
                  />
               </LabeledContainer>

            </div>
         </div>
         <div className="flex items-center justify-end gap-2 px-4 pt-3 pb-1 border-t border-gray-300">
            {
               canApproveOrReject && <>
                  <Button
                     className="rounded-full"
                     variant="contained"
                     size="small"
                     onClick={this.openApproveModal}
                  >
                     Approve
                  </Button>
                  <Button
                     className="rounded-full"
                     variant="outlined"
                     size="small"
                     onClick={this.reject}
                  >
                     Reject
                  </Button>
                  {
                     this.state.approveModalOpen && <Approve
                        open={this.state.approveModalOpen}
                        onClose={this.closeApproveModal}
                        cashRequisition={this.state.cashRequisition}
                     />
                  }
               </>
            }
            <RoundedIconButton
               onClick={this.fetchData}
               color="secondary"
               size="small"
            >
               <Refresh />
            </RoundedIconButton>
         </div>
      </div>

   }
}

const mapStateToProps = (state) => {
   return {
      user: state.user,
   }
}

const CashRequisition = connect(mapStateToProps)(withRouterParams(_CashRequisition));
export default CashRequisition;